import { Box, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import OwnerTab from "../permitPackageTabs/OwnerTab";
import ContractorTab from "../permitPackageTabs/ContractorTab";
import RecordingTab from "../permitPackageTabs/RecordingTab";
import PermitSettings from "../permitPackageTabs/PermitSettings";
import NotarizationTab from "../permitPackageTabs/NotarizationTab";
import CitySiteTab from "../permitPackageTabs/CitySiteTab";
import AdminPackageTab from "../permitPackageTabs/AdminPackageTab";
import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import { useTour } from "@reactour/tour";
import { setRightValue } from "../../../store/actions/uiActions/myPermitsActions";
import { isSubAdminEmail } from "../../../functions/helperFunctions";

const PermitPackageRightContent = ({
  recordLoading,
  handleStartRecord,
  setDocNotaryTypeValue,
  docNotaryTypeValue,
  selectedPermitType,
  isMobile = false,
  isDesktop = false,
  isReadOnlyPage = false,
  display = "flex",
}) => {
  const { rightValue, activeTab, selectedRow } = useSelector(
    (state) => state.myPermitsComponent,
  );
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state?.[currentSession]?.user);
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [] } = subAdmins || {};

  const isAdmin = currentUser?.user?.role === "admin";
  const isSubAdmin = isSubAdminEmail(currentUser?.user?.email, subAdminDomains);
  const isAdminRemoteSession =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("adminRemote") === "true" &&
    Boolean(window.sessionStorage.getItem("adminRemoteEmail"));
  const canAccessAdminTab = isAdmin || isSubAdmin || isAdminRemoteSession;

  const { isOpen } = useTour();
  const dispatch = useDispatch();

  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const resizeTimeout = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 350);

    return () => {
      clearTimeout(resizeTimeout);
    };
  }, [rightValue]);

  useEffect(() => {
    if (!canAccessAdminTab && rightValue === 6) {
      dispatch(setRightValue(0));
    }
  }, [canAccessAdminTab, dispatch, rightValue]);

  const handleRightChange = useCallback(
    (event, newValue) => {
      if (isOpen) return;

      setIsFading(true);
      setTimeout(() => {
        if (rightValue !== newValue) {
          dispatch(dispatch(setRightValue(newValue)));
          setIsFading(false);
        }
      }, 300);
    },
    [isOpen, rightValue, dispatch],
  );

  // Function to determine which component to render
  const getRightTabComponent = () => {
    if (rightValue === 6 && canAccessAdminTab) {
      return <AdminPackageTab />;
    }

    if (activeTab === 1 && selectedRow?.assigned_package?.is_assigned) {
      switch (rightValue) {
        case 0:
          return (
            <OwnerTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 1:
          return <ContractorTab selectedPermitType={selectedPermitType} />;
        default:
          return (
            <OwnerTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
      }
    } else {
      switch (rightValue) {
        case 0:
          return (
            <OwnerTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 1:
          return (
            <ContractorTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 2:
          return (
            <NotarizationTab
              setDocNotaryTypeValue={setDocNotaryTypeValue}
              docNotaryTypeValue={docNotaryTypeValue}
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 3:
          return (
            <RecordingTab
              recordLoading={recordLoading}
              handleStartRecord={handleStartRecord}
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 4:
          return (
            <CitySiteTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        case 5:
          return (
            <PermitSettings
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
        default:
          return (
            <OwnerTab
              selectedPermitType={selectedPermitType}
              isReadOnlyPage={isReadOnlyPage}
            />
          );
      }
    }
  };

  return (
    <Grid
      className="step16 step17 step18 step19 step20 step21"
      size={{ xs: 12, md: 4 }}
      sx={{
        borderLeft: isMobile ? "none" : "4px solid #d3d3d3",
        display,
        flexDirection: "column",
        alignSelf: "stretch",
        flex: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: "100%",
          bgcolor: "background.paper",
          display: "flex",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={rightValue}
          onChange={handleRightChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="scrollable auto tabs example"
          slotProps={{
            indicator: { style: { height: "3px", bottom: "10px" } },
          }}
        >
          <Tab label="Property Owner" disableRipple />
          <Tab label="Contractor" disableRipple />
          <Tab
            label="Notarization"
            disableRipple
            sx={{
              display:
                activeTab === 1 && selectedRow?.assigned_package?.is_assigned
                  ? "none"
                  : undefined,
            }}
          />
          <Tab
            label="Recording"
            disableRipple
            sx={{
              display:
                activeTab === 1 && selectedRow?.assigned_package?.is_assigned
                  ? "none"
                  : undefined,
            }}
          />
          <Tab
            label="City Permit"
            disableRipple
            sx={{
              display:
                activeTab === 1 && selectedRow?.assigned_package?.is_assigned
                  ? "none"
                  : undefined,
            }}
          />
          <Tab
            label="Settings"
            disableRipple
            sx={{
              display:
                activeTab === 1 && selectedRow?.assigned_package?.is_assigned
                  ? "none"
                  : undefined,
            }}
          />
          {canAccessAdminTab && <Tab label="Admin" disableRipple />}
        </Tabs>
      </Box>

      <Box
        sx={{
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          minHeight: 0,
        }}
        className={
          isFading
            ? "fade-exit fade-exit-active"
            : "fade-enter fade-enter-active"
        }
      >
        {getRightTabComponent()}
      </Box>
    </Grid>
  );
};

export default React.memo(PermitPackageRightContent);
